import { fromCallback } from 'xstate';
import { tokenGetter } from '~/utils/auth';
import { WS_APP_SUBPROTOCOL, WS_BEARER_SUBPROTOCOL_PREFIX } from './constants';
import { WebSocketEvents } from './events';
import { WebSocketConfig } from './types';

export const createWebSocketActor = fromCallback<
  WebSocketEvents,
  { config: WebSocketConfig; userId?: string }
>(({ sendBack, input }) => {
  const { config } = input;
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'localhost:8001';
  const wsUrl = baseUrl.replace(/^https?:\/\//, '');
  const protocol = baseUrl.startsWith('https') ? 'wss' : 'ws';

  const url = `${protocol}://${wsUrl}/api${config.endpoint}`;
  let socket: WebSocket | null = null;
  // Set by the cleanup. The token is awaited, so the actor can be stopped (sign-out)
  // before a socket exists; it must then never open one with that account's token.
  let disposed = false;

  const connect = async () => {
    try {
      const token = await tokenGetter.getToken();
      if (disposed) return;
      if (!token) {
        console.error('[WEBSOCKET ACTOR] No auth token available');
        sendBack({ type: 'ON_ERROR', payload: { error: 'No auth token available' } });
        return;
      }
      // MCV-1442: offer a safe app subprotocol for the server to echo, plus
      // the bearer JWT protocol for auth extraction. The server must not echo
      // the JWT-bearing protocol.
      socket = new WebSocket(url, [WS_APP_SUBPROTOCOL, `${WS_BEARER_SUBPROTOCOL_PREFIX}${token}`]);

      socket.onopen = () => {
        sendBack({ type: 'ON_CONNECTED', payload: { socket } });
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // 🔍 WEBSOCKET CONNECTION TRAFFIC LOG

          if (data.type === 'ping' && config.heartbeatEnabled) {
            socket?.send(JSON.stringify({ type: 'pong' }));
            sendBack({ type: 'ON_PING_RECEIVED', payload: data });
          } else {
            sendBack({ type: 'ON_MESSAGE_RECEIVED', payload: data });
          }
        } catch (error) {
          // Log parse errors but don't kill the connection
          console.error('[WEBSOCKET ACTOR] Failed to parse message:', event.data, error);
          // Don't send ON_ERROR for JSON parse errors - they're not fatal
          // Just log and continue processing other messages
        }
      };

      socket.onerror = (errorEvent: Event) => {
        console.warn('[WEBSOCKET ACTOR] WebSocket error event:', errorEvent);
        // Don't immediately send ON_ERROR - let onclose handle reconnection
        // WebSocket errors are often transient and don't need to stop the service
      };

      socket.onclose = () => {
        sendBack({ type: 'ON_DISCONNECTED' });
      };
    } catch (error) {
      console.error('[WEBSOCKET ACTOR] Connection failed:', error);
      sendBack({ type: 'ON_ERROR', payload: { error: String(error) } });
    }
  };

  connect().catch((error) => {
    console.error('[WEBSOCKET ACTOR] Failed to initiate connection:', error);
    sendBack({ type: 'ON_ERROR', payload: { error: String(error) } });
  });

  return () => {
    disposed = true;
    if (socket) {
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    }
  };
});
